import React from 'react';
import _ from 'lodash';

export const SELECTION_MODE = {
  LIST: 'LIST',
  DETAIL: 'DETAIL',
};

function toggleBy(collection, entry, key) {
  const next = [...collection];
  const idx = _.findIndex(next, (i) => i[key] === entry[key]);

  if (idx !== -1) {
    next.splice(idx, 1);
  } else {
    next.push(entry);
  }

  return next;
}

/**
 * DETAIL-mode concern. Owns only its state and mutators; navigation and commit
 * live in the composing hook.
 */
function useDetailSelection() {
  const [activeItem, setActiveItem] = React.useState(null);
  const [selectedInstruments, setSelectedInstruments] = React.useState([]);
  const [isSourceSelected, setIsSourceSelected] = React.useState(false);

  const inDetail = !!activeItem;

  const reset = React.useCallback(() => {
    setActiveItem(null);
    setSelectedInstruments([]);
    setIsSourceSelected(false);
  }, []);

  const drillInto = React.useCallback((item) => setActiveItem(item), []);

  const toggleInstrument = React.useCallback((instrument) => {
    setSelectedInstruments((instruments) => toggleBy(instruments, instrument, 'isin'));
  }, []);

  const selectAllInstruments = React.useCallback((instruments) => {
    setSelectedInstruments(_.isEmpty(instruments) ? [] : instruments);
  }, []);

  const toggleSourceSelected = React.useCallback(() => {
    setIsSourceSelected((selected) => !selected);
  }, []);

  const hasSelection = isSourceSelected || !!selectedInstruments.length;

  return {
    activeItem,
    inDetail,
    selectedInstruments,
    isSourceSelected,
    hasSelection,
    reset,
    drillInto,
    toggleInstrument,
    selectAllInstruments,
    toggleSourceSelected,
  };
}

/**
 * LIST-mode concern. A flow that supports whole-item selection calls this and
 * hands the result to `useSourceSelection` as `items`.
 */
export function useItemSelection() {
  const [selectedItems, setSelectedItems] = React.useState([]);

  const reset = React.useCallback(() => setSelectedItems([]), []);

  const toggleItem = React.useCallback((item) => {
    setSelectedItems((items) => toggleBy(items, item, 'id'));
  }, []);

  // Set membership so list rows don't each scan the array on every render.
  const selectedItemIds = React.useMemo(
    () => new Set(_.map(selectedItems, 'id')),
    [selectedItems]
  );

  const isItemSelected = React.useCallback(
    (item) => selectedItemIds.has(item.id),
    [selectedItemIds]
  );

  const hasSelection = !!selectedItems.length;

  return {
    selectedItems,
    hasSelection,
    reset,
    toggleItem,
    isItemSelected,
  };
}

/**
 * Neutral item-selection for flows without whole-item selection, so the
 * composer never branches on whether `items` was supplied.
 */
const EMPTY_ITEM_SELECTION = Object.freeze({
  selectedItems: [],
  hasSelection: false,
  reset: _.noop,
  toggleItem: _.noop,
  isItemSelected: _.stubFalse,
});

/**
 * Composes the selection concerns and owns the shared orchestration:
 * open-reset, navigation (`goBack`) and commit (`confirm`). The user is always
 * in exactly one mode (LIST or DETAIL). Whether whole-item selection exists is
 * the caller's choice: pass a `useItemSelection()` result as `items`, or omit.
 *
 * @param {object}  [items] Result of `useItemSelection()`.
 * @param {boolean}  open    Resets selection when it changes.
 * @param {Function} onClose Dismisses the modal.
 * @param {Function} onSelectProducts (instruments, sources) => boolean
 */
export function useSourceSelection({items = EMPTY_ITEM_SELECTION, open, onClose, onSelectProducts}) {
  const detail = useDetailSelection();

  const {inDetail, activeItem, selectedInstruments, isSourceSelected} = detail;
  const {selectedItems} = items;

  const resetItems = items.reset;
  const resetDetail = detail.reset;
  React.useEffect(() => {
    resetDetail();
    resetItems();
  }, [open, resetDetail, resetItems]);

  // Detail view returns to the list; the list closes the modal.
  const goBack = React.useCallback(() => {
    if (inDetail) {
      resetDetail();
    } else {
      onClose();
    }
  }, [inDetail, resetDetail, onClose]);

  // Whole-sources come from one channel only: the drilled-in item (DETAIL) or
  // the multi-selected list (LIST).
  const confirm = (closeModal) => () => {
    const selectedSources = inDetail
      ? (isSourceSelected ? [activeItem] : [])
      : selectedItems;
    if (!onSelectProducts(selectedInstruments, selectedSources)) {
      return;
    }
    if (closeModal) {
      onClose();
    } else {
      goBack();
    }
  };

  return {
    mode: inDetail ? SELECTION_MODE.DETAIL : SELECTION_MODE.LIST,
    inDetail,
    activeItem,
    selectedInstruments,
    selectedItems,
    isSourceSelected,
    hasSelection: detail.hasSelection || items.hasSelection,
    drillInto: detail.drillInto,
    toggleInstrument: detail.toggleInstrument,
    selectAllInstruments: detail.selectAllInstruments,
    toggleItem: items.toggleItem,
    isItemSelected: items.isItemSelected,
    toggleSourceSelected: detail.toggleSourceSelected,
    goBack,
    confirm,
  };
}
